/**
 * The list's tab strip and its table header, pinned under the app bar while the
 * page scrolls.
 *
 * WHAT IS PINNED
 * On every `.o-list` screen, the strip(s) between the filter bar and the table
 * -- the stage pipe (Leads, LOS, LMS, Collections, plus Leads' sub-stage row)
 * or the status tabs (Disbursement, Ledger, Payments, NACH, Reports) -- and the
 * column header of each table on the screen. The filter bar scrolls away as
 * before.
 *
 * WHY THE HEADER IS NOT `position: sticky`
 * The strips are: they are direct children of the root, outside every overflow
 * box. The header lives inside `.v-data-table__wrapper`, whose `overflow-x:
 * auto` confines a sticky child to the wrapper, which never scrolls vertically.
 * So this measures how far the table top has gone under the pinned strips and
 * publishes it as `--o-head-shift` on the header; list-screen.scss translates
 * the header by that much (or, where supported, a scroll-driven animation does,
 * from the lengths this publishes on the table). Page height, vertical
 * scrolling and horizontal scrolling (and its proxy bar,
 * behaviours/stickyXScroll.js) are untouched.
 *
 * WHY IT ATTACHES ITSELF
 * Same reason as stickyXScroll: it finds its own targets by structure, so a
 * list screen added later is covered without anyone adding it to a list.
 */

/* Near the root only: a `.v-tabs` nested inside a dialog or a row must not be
   pinned. One level down covers NACH, whose tabs sit in a per-section wrapper
   (which also holds the table, so they stay pinned for its whole length), and
   Reports, whose strip shares an `__head` row with its primary action. */
const STRIPS = [
  ':scope > [class*="__pipe"]',
  ':scope > [class*="__subrow"]',
  ':scope > [class*="__head"]',
  ":scope > .v-tabs",
  ":scope > div > .v-tabs",
].join(", ");
const PIN_CLASS = "o-pin";

let frame = 0;

/* Where supported, the browser moves the header itself, in step with the scroll
   (a scroll-driven animation, list-screen.scss): nothing here runs on scroll,
   so the header cannot lag a frame behind and flicker. This only re-measures
   when the layout changes. Older browsers fall back to a per-frame shift. */
const SCROLL_DRIVEN =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("animation-timeline: view()");

/* How far the header tucks UNDER the last strip. The strip's bottom and the
   header's top are positioned by two different mechanisms (sticky vs a
   translate) and each snaps to device pixels on its own, so meeting edge to edge
   left a hairline seam at most zoom levels -- 0.13px at 100%, more at 110/125% --
   and the rows scrolling beneath flickered through it. The strips sit above the
   header (z-index 5 vs 3), so the overlap is hidden and the seam cannot open. */
const SEAM_TUCK = 1;

function appBarBottom() {
  const bar = document.querySelector(".o-topbar");
  return bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0;
}

/* Lengths stay fractional -- rounding each one is what opened the seam -- but
   are cut to 1/100px so the written value reads back identically and an
   unchanged one is never rewritten. */
function px(n) {
  return Math.round(n * 100) / 100 + "px";
}

/* Writes only on a change. Most of these sit on an element above the whole
   table, so a rewrite restyles every cell under it. */
function setProp(el, name, value) {
  if (el.style.getPropertyValue(name) !== value) el.style.setProperty(name, value);
}

function syncRoot(root, barBottom) {
  // Strips stack in page order, each pinned under the ones above it. Measured
  // every time: a strip grows when it opens (Collections' breakdown), and the
  // count line wraps on a narrow window. The exact height, not `offsetHeight`,
  // which is rounded and let a gap or an overlap build up between strips.
  let top = barBottom;
  root.querySelectorAll(STRIPS).forEach((strip) => {
    strip.classList.add(PIN_CLASS);
    setProp(strip, "top", px(top));
    top += strip.getBoundingClientRect().height;
  });
  // The line the header stops at: under the strips, once they are pinned.
  // Until they are, the table is still below them and the header stays put.
  const pinTop = Math.max(0, top - SEAM_TUCK);
  setProp(root, "--o-pin-top", px(pinTop));

  root.querySelectorAll(".v-data-table").forEach((dt) => {
    const table = dt.querySelector(".v-data-table__wrapper > table");
    const head = table && table.tHead;
    if (!head) return;
    const tBox = table.getBoundingClientRect();
    // Never past the last row, or the header would slide out under the pager.
    const room = Math.max(0, tBox.height - head.getBoundingClientRect().height);
    if (SCROLL_DRIVEN) {
      // Scroll-independent: offsets within the table block, not positions.
      const start = tBox.top - dt.getBoundingClientRect().top;
      setProp(dt, "--o-head-start", px(start));
      setProp(dt, "--o-head-end", px(start + room));
      setProp(dt, "--o-head-room", px(room));
    } else {
      // On the header itself, which is the only thing that reads it. On the
      // table it was inherited by every cell, so each scroll frame restyled the
      // whole table and the header landed a frame late and shook.
      const shift = Math.min(Math.max(0, pinTop - tBox.top), room);
      setProp(head, "--o-head-shift", px(shift));
    }
  });
}

function syncAll() {
  frame = 0;
  const roots = document.querySelectorAll(".o-list");
  if (!roots.length) return;
  const barBottom = appBarBottom();
  roots.forEach((root) => syncRoot(root, barBottom));
}

function schedule() {
  if (!frame) frame = requestAnimationFrame(syncAll);
}

export default {
  install(Vue, { router } = {}) {
    if (typeof window === "undefined") return;

    if (!SCROLL_DRIVEN) {
      window.addEventListener("scroll", schedule, { passive: true });
    }
    window.addEventListener("resize", schedule, { passive: true });
    if (router) router.afterEach(() => Vue.nextTick(schedule));

    /* Tables are rebuilt on every reload (`v-if="!table_loading"`), which drops
       the shift, and strips open and close without a scroll; one measure per
       frame, whatever changed. */
    if (typeof MutationObserver !== "undefined") {
      new MutationObserver(schedule).observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  },
};
